import { useState, type FunctionComponent } from "react";
import Grid from "@mui/material/Grid";
import { useTranslation } from "react-i18next";

import { type IncidentReportRiskAssessmentParticipant, type IncidentReportRiskAssessmentCore } from "components/CriticalIncident/interfaces";
import { ReportSectionAccordion } from "components/CriticalIncident/components/Shared/ReportSectionAccordion";
import { EMPTY_PARTICIPANT } from "../../RiskAssessments.constants";
import { useRiskAssessmentFieldActions } from "../../hooks/useRiskAssessmentFieldActions";
import { RiskAssessmentFieldActionsProvider } from "../../hooks/RiskAssessmentFieldActionsContext";
import { ParticipantGroupAccordion } from "../ParticipantGroupAccordion";
import { PersonalInfoSection } from "../PersonalInfoSection";
import { RiskFactorsSection } from "../RiskFactorsSection";
import { ActionsTakenSection } from "../ActionsTakenSection";

export type ParticipantGroupKey = "personalInfo" | "riskFactors" | "actionsTaken";

const PERSONAL_INFO_FIELDS: (keyof IncidentReportRiskAssessmentParticipant)[] = ["name", "dateOfBirth", "phoneNumber", "emailAddress"];

const CORE_GROUP_FIELDS: Record<"riskFactors" | "actionsTaken", (keyof IncidentReportRiskAssessmentCore)[]> = {
  riskFactors: [
    "riskOfSuicide",
    "suicideRiskFactors",
    "riskOfSelfHarm",
    "selfHarmRiskFactors",
    "riskOfHarmToFromOthers",
    "harmToFromOthersRiskFactors",
    "riskDescription",
    "riskFactors",
  ],
  actionsTaken: [
    "hasDutyToReport",
    "dutyToReportDescription",
    "notifiedPartiesDetails",
    "measureTakenToReduceRisks",
    "agreementAgainstSuicide",
    "agreementAgainstSuicideDescription",
    "selectedNotifiedParties",
    "partiesNotified",
  ],
};

export interface ParticipantAccordionProps {
  index: number;
  participant: IncidentReportRiskAssessmentParticipant;
  isReadonly: boolean;
  removeAction?: () => void;
  removeHidden: boolean;
  patchParticipant: (partial: Partial<IncidentReportRiskAssessmentParticipant>) => void;
  expanded: boolean;
  onToggle: (isExpanded: boolean) => void;
}

export const ParticipantAccordion: FunctionComponent<ParticipantAccordionProps> = ({
  index,
  participant,
  isReadonly,
  removeAction,
  removeHidden,
  patchParticipant,
  expanded,
  onToggle,
}) => {
  const { t } = useTranslation("criticalIncident");
  const [expandedGroups, setExpandedGroups] = useState<Partial<Record<ParticipantGroupKey, boolean>>>({});
  const basePath = `riskAssessment.participants[${index}]`;
  const fieldActions = useRiskAssessmentFieldActions(basePath, participant, patchParticipant, isReadonly);
  const { recordErrors } = fieldActions;

  const groupHasErrors = (group: ParticipantGroupKey) => {
    const fields = group === "personalInfo" ? PERSONAL_INFO_FIELDS : CORE_GROUP_FIELDS[group];
    return fields.some((field) => Boolean(recordErrors?.[field as string]));
  };

  const toggleGroup = (group: ParticipantGroupKey) => (isExpanded: boolean) => {
    setExpandedGroups((prev) => ({ ...prev, [group]: isExpanded }));
  };

  return (
    <ReportSectionAccordion
      label={t("participant_number", { number: index + 1 })}
      name={basePath}
      pristineValue={EMPTY_PARTICIPANT}
      removeAction={isReadonly ? undefined : removeAction}
      expanded={expanded}
      onToggle={onToggle}
      readonly={isReadonly}
      removeHidden={removeHidden}
    >
      <Grid container spacing={2}>
        <Grid item sm={12}>
          <ParticipantGroupAccordion
            label={t("personal_information")}
            expanded={Boolean(expandedGroups.personalInfo)}
            onToggle={toggleGroup("personalInfo")}
            hasErrors={groupHasErrors("personalInfo")}
            readonly={isReadonly}
          >
            <PersonalInfoSection
              basePath={basePath}
              participant={participant}
              touched={fieldActions.recordTouched}
              errors={fieldActions.recordErrors}
              isReadonly={isReadonly}
              patchRecord={patchParticipant}
            />
          </ParticipantGroupAccordion>
        </Grid>

        <Grid item sm={12}>
          <ParticipantGroupAccordion
            label={t("risk_factors_group")}
            expanded={Boolean(expandedGroups.riskFactors)}
            onToggle={toggleGroup("riskFactors")}
            hasErrors={groupHasErrors("riskFactors")}
            readonly={isReadonly}
          >
            <RiskAssessmentFieldActionsProvider value={fieldActions}>
              <RiskFactorsSection />
            </RiskAssessmentFieldActionsProvider>
          </ParticipantGroupAccordion>
        </Grid>

        <Grid item sm={12}>
          <ParticipantGroupAccordion
            label={t("actions_taken")}
            expanded={Boolean(expandedGroups.actionsTaken)}
            onToggle={toggleGroup("actionsTaken")}
            hasErrors={groupHasErrors("actionsTaken")}
            readonly={isReadonly}
          >
            <RiskAssessmentFieldActionsProvider value={fieldActions}>
              <ActionsTakenSection />
            </RiskAssessmentFieldActionsProvider>
          </ParticipantGroupAccordion>
        </Grid>
      </Grid>
    </ReportSectionAccordion>
  );
};
